import React from 'react';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon, 
  UserGroupIcon, 
  BookOpenIcon, 
  TrophyIcon, 
  HeartIcon, 
  LightBulbIcon, 
  ShieldCheckIcon,
  CalendarIcon,
  GlobeAltIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  StarIcon,
  UsersIcon,
  BuildingLibraryIcon,
  ClockIcon,
  ArrowRightIcon,
  SparklesIcon,
  UserIcon,
  HomeModernIcon,
  MapPinIcon,
  MusicalNoteIcon,
  PaintBrushIcon,
  MicrophoneIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';

const About = () => {
  const coreValues = [
    { 
      icon: HeartIcon, 
      title: 'Honesty', 
      description: 'We instill truthfulness and integrity in every student.',
      color: 'from-pink-500 to-rose-500'
    },
    { 
      icon: ShieldCheckIcon, 
      title: 'Integrity', 
      description: 'Building character with strong moral and ethical values.',
      color: 'from-green-500 to-emerald-500'
    },
    { 
      icon: SparklesIcon, 
      title: 'Spirituality', 
      description: 'Nurturing the soul with spiritual wisdom and mindfulness.',
      color: 'from-purple-500 to-indigo-500'
    },
    { 
      icon: BookOpenIcon, 
      title: 'Academic Excellence', 
      description: 'Delivering quality education with modern teaching methods.',
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      icon: LightBulbIcon, 
      title: 'Creativity', 
      description: 'Encouraging innovation and creative thinking in every child.',
      color: 'from-yellow-500 to-orange-500'
    },
    { 
      icon: UserGroupIcon, 
      title: 'Social Responsibility', 
      description: 'Developing responsible citizens who contribute to society.',
      color: 'from-red-500 to-pink-500'
    },
    { 
      icon: UserIcon, 
      title: 'Personal Development', 
      description: 'Focus on holistic growth - mind, body, and soul.',
      color: 'from-teal-500 to-cyan-500'
    }
  ];

  const milestones = [
    { year: '2006', title: 'The Beginning', description: 'Vidya Bharati Public School was established with a vision to provide quality education in Bhalki.', icon: CalendarIcon },
    { year: '2008', title: 'Campus Development', description: '16,800 sq. ft. campus building constructed spread across 2.5 acres of land.', icon: HomeModernIcon },
    { year: '2010', title: 'Sports Excellence', description: '5 dedicated sports courts for athletics, cricket, football, basketball, and indoor games.', icon: TrophyIcon },
    { year: '2015', title: 'Smart Education', description: 'Introduced digital learning with smart classrooms and modern teaching aids.', icon: LightBulbIcon },
    { year: '2023', title: 'Community Impact', description: 'Recognized as a leading educational institution in the region.', icon: StarSolidIcon }
  ];

  const facilities = [
    { icon: BuildingLibraryIcon, title: '16,800 sq. ft. Campus', description: 'Modern infrastructure with spacious classrooms and facilities.' },
    { icon: MapPinIcon, title: '2.5 Acres Campus', description: 'Spread across 2.5 acres with lush greenery and open spaces.' },
    { icon: TrophyIcon, title: '5 Sports Courts', description: 'Dedicated courts for cricket, football, basketball, athletics, and indoor sports.' },
    { icon: UserGroupIcon, title: '40:1 Student Ratio', description: 'Teacher-student ratio of 40:1 for personalized attention.' },
    { icon: AcademicCapIcon, title: 'Expert Faculty', description: 'Highly qualified and experienced teachers for every subject.' }
  ];

  const values = [
    { title: 'Honesty', description: 'Truthfulness and transparency in all actions' },
    { title: 'Integrity', description: 'Strong moral principles and ethical conduct' },
    { title: 'Spirituality', description: 'Connecting with inner self and universal values' },
    { title: 'Academic Excellence', description: 'Striving for the highest educational standards' },
    { title: 'Creativity', description: 'Innovative thinking and creative expression' },
    { title: 'Social Responsibility', description: 'Contributing positively to society and community' },
    { title: 'Personal Development', description: 'Holistic growth of mind, body, and spirit' }
  ];

  const whyChooseUs = [
    { 
      icon: AcademicCapIcon, 
      title: 'Quality Education', 
      description: '98% success rate with top results in board examinations.',
      color: 'from-blue-500 to-blue-600'
    },
    { 
      icon: UserGroupIcon, 
      title: '40:1 Student Ratio', 
      description: 'Personalized attention with 40 students per teacher.',
      color: 'from-green-500 to-green-600'
    },
    { 
      icon: ShieldCheckIcon, 
      title: 'Safe Environment', 
      description: 'Secure campus with CCTV surveillance and caring staff.',
      color: 'from-purple-500 to-purple-600'
    },
    { 
      icon: TrophyIcon, 
      title: 'Excellence Record', 
      description: '15+ years of educational excellence in Bhalki.',
      color: 'from-orange-500 to-orange-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-yellow-300 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="inline-block bg-white/20 backdrop-blur-lg rounded-full px-6 py-2 mb-4">
            <span className="text-sm font-medium">🏫 About Us</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            Vidya Bharati <span className="text-yellow-300">Public School</span>
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto font-light">
            Est. 2006 • Bhalki, Karnataka
          </p>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto mt-4">
            "Words don't do justice to a school like VBPS Bhalki. 
            Let's look at the numbers that define our legacy."
          </p>
        </div>
      </section>

      {/* Stats Section - VBPS at a Glance */}
      <section className="py-16 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white/20 to-transparent"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-white/20 backdrop-blur-lg rounded-full px-6 py-2 text-sm font-medium">
              📊 VBPS Bhalki at a Glance
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4">The Numbers That <span className="text-yellow-300">Define Us</span></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 text-center border border-white/20 hover:bg-white/20 transition">
              <div className="text-4xl md:text-5xl font-bold text-yellow-300 mb-2">2006</div>
              <div className="text-blue-100 text-sm">Year of Establishment</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 text-center border border-white/20 hover:bg-white/20 transition">
              <div className="text-4xl md:text-5xl font-bold text-yellow-300 mb-2">40:1</div>
              <div className="text-blue-100 text-sm">Student-Teacher Ratio</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 text-center border border-white/20 hover:bg-white/20 transition">
              <div className="text-4xl md:text-5xl font-bold text-yellow-300 mb-2">5</div>
              <div className="text-blue-100 text-sm">Sports Courts</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 text-center border border-white/20 hover:bg-white/20 transition">
              <div className="text-4xl md:text-5xl font-bold text-yellow-300 mb-2">16,800</div>
              <div className="text-blue-100 text-sm">Sq. Ft. Campus</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 text-center border border-white/20 hover:bg-white/20 transition md:col-span-2">
              <div className="text-4xl md:text-5xl font-bold text-yellow-300 mb-2">2.5</div>
              <div className="text-blue-100 text-sm">Acres Campus Area</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 text-center border border-white/20 hover:bg-white/20 transition md:col-span-2">
              <div className="text-4xl md:text-5xl font-bold text-yellow-300 mb-2">15+</div>
              <div className="text-blue-100 text-sm">Years of Excellence</div>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-sm font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-4 py-1 rounded-full">Infrastructure</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3">
              Our <span className="text-blue-600">Campus</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto rounded-full mt-2"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Spread across 2.5 acres with 16,800 sq. ft. of built-up area, our campus is designed for holistic learning.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {facilities.map((facility, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100">
                <div className="inline-block p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl mb-4">
                  <facility.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">{facility.title}</h3>
                <p className="text-gray-600 text-sm">{facility.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-sm font-bold text-purple-600 uppercase tracking-wider bg-purple-50 px-4 py-1 rounded-full">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3">
              The 7 Pillars of <span className="text-purple-600">VBPS</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-pink-600 mx-auto rounded-full mt-2"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Our moral values shape the character and future of every student
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {coreValues.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 text-center group">
                  <div className={`inline-block p-4 rounded-2xl bg-gradient-to-r ${value.color} mb-4 shadow-lg group-hover:scale-110 transition`}>
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-sm font-bold text-green-600 uppercase tracking-wider bg-green-50 px-4 py-1 rounded-full">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3">
              Why <span className="text-green-600">VBPS Bhalki</span>?
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-emerald-600 mx-auto rounded-full mt-2"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="bg-gray-50 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <div className={`inline-block p-4 rounded-2xl bg-gradient-to-r ${item.color} mb-4`}>
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-sm font-bold text-orange-600 uppercase tracking-wider bg-orange-50 px-4 py-1 rounded-full">Timeline</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3">
              Our <span className="text-orange-600">Journey</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-600 to-yellow-600 mx-auto rounded-full mt-2"></div>
          </div>

          <div className="max-w-4xl mx-auto relative">
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-blue-600 to-purple-600 h-full"></div>
            
            {milestones.map((milestone, index) => {
              const IconComponent = milestone.icon;
              return (
                <div key={index} className={`flex flex-col md:flex-row items-center mb-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className="md:w-1/2 p-4">
                    <div className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl">
                          <IconComponent className="h-5 w-5 text-white" />
                        </div>
                        <span className="text-sm font-bold text-blue-600">{milestone.year}</span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-800">{milestone.title}</h3>
                      <p className="text-gray-600 text-sm">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="md:w-12 flex justify-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  </div>
                  <div className="md:w-1/2 hidden md:block"></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">
              Join the <span className="text-yellow-300">VBPS Family</span>
            </h2>
            <p className="text-blue-100 mb-8">
              Give your child the best education at Vidya Bharati Public School, Bhalki.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/admissions" 
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center gap-2 hover:scale-105"
              >
                Apply for Admission
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
              <Link 
                to="/contact" 
                className="bg-white/20 backdrop-blur-lg text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/30 transition border border-white/20"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
